import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import it from './locales/it.json';
import en from './locales/en.json';

const resources = {
  it: { translation: it },
  en: { translation: en }
};

// Check if user has a saved language preference (localStorage or cookie)
const getSavedLanguage = (): string | null => {
  // 1) localStorage (preferred)
  try {
    const saved = localStorage.getItem('i18nextLng');
    if (saved) return saved;
  } catch {
    // ignore
  }

  // 2) cookie fallback (for browsers/environments where localStorage is blocked)
  try {
    const match = document.cookie.match(/(?:^|; )df_lang=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

// Detect country via IP geolocation and set language accordingly
export const detectCountryAndSetLanguage = async (): Promise<void> => {
  // Skip if user already has a saved preference
  if (getSavedLanguage()) {
    console.log('User has saved language preference, skipping geolocation');
    return;
  }

  try {
    const response = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(3000) // 3 second timeout
    });
    
    if (!response.ok) {
      console.warn('Geolocation API returned non-OK status');
      return;
    }

    const data = await response.json();
    const countryCode = data.country_code;
    
    console.log('Detected country:', countryCode);
    
    // Set English for non-Italian countries
    if (countryCode && countryCode !== 'IT') {
      i18n.changeLanguage('en');
      console.log('Language set to English based on geolocation');
    } else {
      i18n.changeLanguage('it');
      console.log('Language set to Italian based on geolocation');
    }
  } catch (error) {
    console.warn('Geolocation detection failed, using default language:', error);
    // Fallback to Italian if geolocation fails
    i18n.changeLanguage('it');
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'it',
    supportedLngs: ['it', 'en'],
    load: 'languageOnly', // Use 'en' instead of 'en-US'
    
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
      convertDetectedLanguage: (lng: string) => lng.split('-')[0] // Extract base language code
    },
    
    interpolation: {
      escapeValue: false // React already escapes values
    },
    
    react: {
      useSuspense: false
    }
  });

export default i18n;
